import { SettingOutlined } from "@ant-design/icons";
import { Button, Popover, Switch } from "antd";
import { useContext } from "react";
import { ViewerContext } from "../../page/Viewer";
import styled from "styled-components";

type ViewerMode = "image" | "slide";
interface ViewerToggleManagerProps {
  mode?: ViewerMode;
}

const ViewerToggleManager = ({ mode = "slide" }: ViewerToggleManagerProps) => {
  /* state */
  const { userCustomSet, setUserCustomSet } = useContext(ViewerContext);

  /* handler fn */
  const handleUseScaleBar = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useScaleBar: checked });
  };

  const handleUseMagnificationBar = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useMagnificationBar: checked });
  };

  const handleUseViewerThumbnail = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useViewerThumbnail: checked });
  };

  const handleUseViewerRotationSlider = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useViewerRotationSlider: checked });
  };

  const handleUseGammaSlider = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useGammaSlider: checked });
  };

  const handleUseCaptureButton = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useCaptureButton: checked });
  };

  const handleUseICCButton = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useICCButton: checked });
  };

  const handleUseAnnotation = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useAnnotation: checked });
  };

  const handleUseInvertedZoom = (checked: boolean) => {
    setUserCustomSet({ ...userCustomSet, useInvertedZoom: checked });
  };

  const handleAllVisible = (checked: boolean) => {    
    if (mode === "image") {
      setUserCustomSet({
        ...userCustomSet,
        useCaptureButton: checked,
        useViewerRotationSlider: checked,
        useGammaSlider: checked,
        useViewerThumbnail: checked,
        useAnnotation: checked,
        useInvertedZoom: checked,
      });
    } else {
      setUserCustomSet({
        useScaleBar: checked,
        useMagnificationBar: checked,
        useCaptureButton: checked,
        useViewerRotationSlider: checked,
        useGammaSlider: checked,
        useViewerThumbnail: checked,
        useAnnotation: checked,
        useICCButton: checked,
        useInvertedZoom: checked,
        userGammaValue: userCustomSet?.userGammaValue ?? 1
      });
    }
};

  const popoverRender = () => {
    return (
      <PopoverRender>
        <section>
          <span>Show All</span>
          <Switch
            defaultChecked
            checked={
              mode === "image" ?
                  userCustomSet?.useCaptureButton &&
                  userCustomSet?.useViewerRotationSlider &&
                  userCustomSet?.useGammaSlider &&
                  userCustomSet?.useAnnotation &&
                  userCustomSet?.useViewerThumbnail &&
                  userCustomSet?.useInvertedZoom
              :
                userCustomSet?.useScaleBar &&
                userCustomSet?.useMagnificationBar &&
                userCustomSet?.useCaptureButton &&
                userCustomSet?.useViewerRotationSlider &&
                userCustomSet?.useGammaSlider &&
                userCustomSet?.useViewerThumbnail &&
                userCustomSet?.useAnnotation &&
                userCustomSet?.useICCButton &&
                userCustomSet?.useInvertedZoom
            }
            onChange={handleAllVisible}
          />
        </section>

        {/* 이미지 모드에서도 표시되는 항목들 */} 
        <section>
          <span>Show Capture Button</span>        
          <Switch
            defaultChecked
            checked={userCustomSet?.useCaptureButton}
            onChange={handleUseCaptureButton}     
          />
        </section>
        <section>
          <span>Show Rotation Slider</span>       
          <Switch
            defaultChecked
            checked={userCustomSet?.useViewerRotationSlider}
            onChange={handleUseViewerRotationSlider}
          />
        </section>
        <section>
          <span>Show Gamma Slider</span>
          <Switch
            defaultChecked
            checked={userCustomSet?.useGammaSlider}
            onChange={handleUseGammaSlider}       
          />
        </section>
        <section>
          <span>Show Image Thumbnail</span>       
          <Switch
            defaultChecked
            checked={userCustomSet?.useViewerThumbnail}
            onChange={handleUseViewerThumbnail}   
          />
        </section>
        <section>
          <span>Show Annotations</span>       
          <Switch
            defaultChecked
            checked={userCustomSet?.useAnnotation}
            onChange={handleUseAnnotation}    
          />
        </section>
        <section>
          <span>Invert Scroll Zoom</span>       
          <Switch
            checked={userCustomSet?.useInvertedZoom}
            onChange={handleUseInvertedZoom}    
          />
        </section>

        {/* 슬라이드 모드에서만 표시 */}
        {mode === "slide" && (
          <>
            <section>
              <span>Show Scale Bar</span>
              <Switch
                defaultChecked
                checked={userCustomSet?.useScaleBar}
                onChange={handleUseScaleBar}      
              />
            </section>
            <section>
              <span>Show Magnification Bar</span> 
              <Switch
                defaultChecked
                checked={userCustomSet?.useMagnificationBar}
                onChange={handleUseMagnificationBar}
              />
            </section>
            <section>
              <span>Show ICC Button</span>        
              <Switch
                defaultChecked
                checked={userCustomSet?.useICCButton}
                onChange={handleUseICCButton}     
              />
            </section>
          </>
        )}
      </PopoverRender>
    );
  };

  return (
    <>
      <Popover content={popoverRender} trigger={["hover"]}>
        <StyledSettingsButton icon={<SettingOutlined />}>
          Settings
        </StyledSettingsButton>
      </Popover>
    </>
  );
};

export default ViewerToggleManager;

const StyledSettingsButton = styled(Button)`
  background-color: rgba(0, 0, 0, 0.5) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
  border-radius: 4px;
  font-size: 0.6rem;
  height: 1.45rem;
  padding: 0 0.4rem;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }

  .ant-btn-icon {
    display: flex;
    align-items: center;
  }

  span:not(.ant-btn-icon) {
    display: flex;
    align-items: center;
  }
`;

const PopoverRender = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 13rem;

  section {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  section:not(:last-child) {
    padding: 0 0 0.5rem 0;
    border-bottom: 1px solid #e8e8e8;
  }

  section:first-child {
    border-bottom: 1px solid black;
  }
`;
